import { Component, Input, Output, EventEmitter } from '@angular/core';
import { TranslationService } from '../shared/services/translation.service';
import { TranslateService } from '@ngx-translate/core';
import { CsvHeader } from './interface/csvHeader';
import { ProgressStateService } from '../shared/services/progressstate.service';
import { JobStatus, JobStatusDetail } from '../shared/models/progress.model';
import { ProgressService } from '../shared/services/progress.service';
import { CommonConstants } from '../shared/constants/common.constants';
import { AppConfigService } from '../core/app-config/app-config.service';
import { SessionStorageService } from '../shared/services/session-storage.service';
import { CsvRowKeys } from '../shared/constants/csv.constants';
@Component({
  selector: 'app-file-download',
  imports: [],
  templateUrl: './file-download.component.html'
})
export class FileDownloadComponent {
  private process: JobStatusDetail[] = [];
  public importexportStatus = JobStatus;
  private downloadCount = 0;
  @Input() public externalData: any[] = [];
  @Output() public downloadSuccess = new EventEmitter<void>();
  constructor(
    public translationService: TranslationService,
    private readonly translateService: TranslateService,
    private readonly progressStateService: ProgressStateService,
    private readonly progressService: ProgressService,
    private readonly appConfigService: AppConfigService,
    private readonly sessionStorageService: SessionStorageService
  ) {}

  public async downloadFile(): Promise<void> {
    try {
      const currentLang =
        this.sessionStorageService.getItem<string>(CommonConstants.SESSION_LANGUAGE_CODE) ??
        this.appConfigService.configuration.defaultLanguage;
      const baseRow: CsvHeader = {
        UniqueID: `<<${this.translateService.instant('vbeiUserDefinedUniqueIdGoesHere')}>>`,
        LimitSetName: '',
        RowType: '',
        RuleType: '',
        FilterName: '',
        FilterValue: '',
        Account: '',
        TranslationLanguage: '',
        TranslationValue: '',
        Limits: '',
        Units: '',
        Compliancelevel: '',
        Template: ''
      };
      const transformedRows: CsvHeader[] = this.externalData.map((item) => {
        if (item.type === 'testAndParameter') {
          return {
            ...baseRow,
            LimitSetName: `<<${this.translateService.instant('vbeiLimitSetNameGoesHere')}>>`,
            RowType: 'Test - Parameter',
            FilterName: this.formatCodeAndName(item.testCode, item.testName),
            FilterValue: this.formatCodeAndName(item.parameterCode, item.parameterName)
          };
        } else {
          return {
            ...baseRow,
            LimitSetName: `<<${this.translateService.instant('vbeiLimitSetNameGoesHere')}>>`,
            RowType: 'Parameter',
            FilterValue: this.formatCodeAndName(item.parameterCode, item.parameterName)
          };
        }
      });
      const data: CsvHeader[] = [
        {
          ...baseRow,
          RowType: 'Definition',
          RuleType: 'Custom'
        },
        {
          ...baseRow,
          RowType: 'Translation',
          TranslationLanguage: this.appConfigService.configuration.defaultLanguage,
          TranslationValue: `<<${this.translateService.instant('vbeiReferenceNameGoesHere')}>>`
        },
        ...(currentLang !== this.appConfigService.configuration.defaultLanguage
          ? [
              {
                ...baseRow,
                RowType: 'Translation',
                TranslationLanguage: currentLang,
                TranslationValue: `<<${this.translateService.instant('vbeiReferenceNameTranslationGoesHere')}>>`
              }
            ]
          : []),
        {
          ...baseRow,
          RowType: 'StyleTemplate',
          Template: `<<${this.translateService.instant('vbeiStyleTemplateNameGoesHere')}>>`
        },
        {
          ...baseRow,
          RowType: 'Account',
          Account: `<<${this.translateService.instant('vbeiAccountCodesGoesHere')}>>`
        },
        ...transformedRows,
        {
          ...baseRow,
          LimitSetName: `<<${this.translateService.instant('vbeiLimitSetNameGoesHere')}>>`,
          RowType: 'Scope',
          FilterName: `<<${this.translateService.instant('vbeiScopeTypesGoesHere')}>>`,
          FilterValue: `<<${this.translateService.instant('vbeiScopeCodesGoesHere')}>>`
        }
      ];

      const headers = await this.translationService.getTranslatedHeaders(currentLang);

      const csv = await this.generateCSV(data, headers);
      const fileName = this.getUniqueFileName();

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
      setTimeout(() => {
        this.downloadSuccess.emit();
        this.process = this.progressStateService.getProcess();
        const obj = new JobStatusDetail();
        obj.name = this.translateService.instant('vbeiDownloadLimitSetTemplate');
        obj.id = 'DownloadLimitSetTemplate';
        obj.status = this.importexportStatus.Completed;
        this.process.push(obj);
        this.progressService.feedProcess(this.process);
        this.progressStateService.setProcess(this.process);
      }, 500);
    } catch {
      this.process = this.progressStateService.getProcess();
      const obj = new JobStatusDetail();
      obj.name = this.translateService.instant('vbeiDownloadLimitSetTemplate');
      obj.id = 'DownloadLimitSetTemplate';
      obj.status = this.importexportStatus.Failed;
      this.process.push(obj);
      this.progressService.feedProcess(this.process);
      this.progressStateService.setProcess(this.process);
    }
  }

  public async generateCSV(data: CsvHeader[], translatedHeaders: string[]): Promise<string> {
    const csvHeaders: string[] = [];
    csvHeaders.push(translatedHeaders.map((h) => `"${h.replace(/"/g, '""')}"`).join(','));
    for (const row of data) {
      const values = await Promise.all(
        CsvRowKeys.map(async (key) => {
          let value = row[key] ?? '';

          if (key === 'RowType' && value) {
            try {
              value = await this.translationService.getUserLanguageTranslation(value);
            } catch (error) {
              console.error(`Translation failed for RowType value: ${value}`, error);
            }
          }
          if (key === 'RuleType' && row.RowType === 'Definition' && value) {
            try {
              value = await this.translationService.getUserLanguageTranslation(value);
            } catch (error) {
              console.error(`Translation failed for RuleType: ${value}`, error);
            }
          }

          if (typeof value === 'string') {
            if (
              this.sessionStorageService.getItem<string>(CommonConstants.SESSION_LANGUAGE_CODE) !==
              this.appConfigService.configuration.defaultLanguage
            ) {
              value = await this.translatePlaceholders(value);
            }
            value = value.replace(/"/g, '""');
          }

          return `"${value}"`;
        })
      );

      csvHeaders.push(values.join(','));
    }

    return '\uFEFF' + csvHeaders.join('\n');
  }

  public getUniqueFileName(): string {
    const baseFileName = CommonConstants.LIMITSETTEMPLATE;
    this.downloadCount++;
    return this.downloadCount === 1 ? `${baseFileName}.csv` : `${baseFileName} (${this.downloadCount}).csv`;
  }

  public formatCodeAndName(code: string, name: string): string {
    const codePart = code && code !== null ? `[${code}]` : '';
    const namePart = name && name !== null ? ` - ${name}` : '';
    return codePart + namePart;
  }

  public async translatePlaceholders(value: string): Promise<string> {
    const regex = /<<(.+?)>>/g;
    let match;
    let translatedText = value;

    while ((match = regex.exec(value)) !== null) {
      const original = match[0];
      const toTranslate = match[1];
      try {
        const translated = await this.translationService.getUserLanguageTranslation(toTranslate);
        translatedText = translatedText.replace(original, `<<${translated}>>`);
      } catch (error) {
        console.error(`Translation failed for ${toTranslate}`, error);
      }
    }
    return translatedText;
  }
}
